import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
} from '@mui/material'
import React, { useEffect } from 'react'
import { FormikProps } from 'formik'

interface DealerGroupData {
  dealerGroupNumber: string
  dealerIdList: {
    dealerId: string
  }[]
}

interface FormValues {
  email: string
  firstName: string
  lastName: string
  phone: string
  dealerGroupData: DealerGroupData[]
  aftermarketClient: string[]
}

interface Props {
  openAddDealerGroup: boolean
  onCloseAddDealerGroup: () => void
  formik: FormikProps<FormValues>
  dealerGroupNumberList: { dealerGroupName: string; dealerGroupNumber: string }[]
  aftermarketClientList: { clientId: string; dealerGroupNumber: string; client: string }[]
}

const styles = {
  container: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '12px',
  },
  headerStyle: {
    borderRadius: '12px',
    display: 'flex',
    p: 2,
    justifyContent: 'center',
    backgroundColor: 'white',
    mb: 4,
  },
  selectMenu: {
    maxHeight: 200,
    marginTop: '8px',
  },
  formControlStyles: {
    width: '90%',
    margin: 'auto',
  },
}

function UserAftermarketClientAdd(props: Props) {
  const {
    openAddDealerGroup,
    onCloseAddDealerGroup,
    formik,
    dealerGroupNumberList,
    aftermarketClientList,
  } = props

  useEffect(() => {
    if (!Array.isArray(aftermarketClientList) || aftermarketClientList.length === 0) {
      return
    }

    const currentValues = formik.values.aftermarketClient || []

    const normalizedIds = currentValues
      .map((value) => {
        // Already a client ID
        const clientById = aftermarketClientList.find(
          (item) => String(item.clientId) === String(value),
        )

        if (clientById) {
          return String(clientById.clientId)
        }

        // Existing value is a client name
        const clientByName = aftermarketClientList.find(
          (item) => item.client?.trim() === String(value).trim(),
        )

        return clientByName ? String(clientByName.clientId) : null
      })
      .filter((id): id is string => id !== null)

    const currentIds = currentValues.map(String)

    const changed =
      normalizedIds.length !== currentIds.length ||
      normalizedIds.some((id, index) => id !== currentIds[index])

    if (changed) {
      formik.setFieldValue('aftermarketClient', normalizedIds)
    }
  }, [aftermarketClientList, formik.values.aftermarketClient])
  return (
    <Modal
      open={openAddDealerGroup}
      onClose={onCloseAddDealerGroup}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <Box sx={styles.container}>
        <Box sx={styles.headerStyle}>
          <Typography id='modal-modal-title' variant='h5' component='h2' color='black'>
            Aftermarket Clients
          </Typography>
        </Box>
        <Grid xs={12}>
          <Grid item sx={styles.formControlStyles}>
            <FormControl fullWidth>
              <InputLabel
                sx={{
                  backgroundColor: 'white',
                  paddingLeft: 1,
                  paddingRight: 1,
                  '&.Mui-focused': {
                    color: 'black',
                  },
                }}
              >
                Aftermarket Client Name
              </InputLabel>
              <Select
                labelId='aftermarketClient'
                id='aftermarketClient'
                name='aftermarketClient'
                multiple
                value={(formik.values.aftermarketClient || []).map(String)}
                onChange={(event) => {
                  const value = event.target.value

                  formik.setFieldValue(
                    'aftermarketClient',
                    typeof value === 'string' ? value.split(',') : value.map(String),
                  )
                }}
                renderValue={(selected) =>
                  Array.isArray(selected)
                    ? selected
                        .map((value) => {
                          const client = aftermarketClientList.find(
                            (item) => String(item.clientId) === String(value),
                          )

                          return client?.client || ''
                        })
                        .filter(Boolean)
                        .join(', ')
                    : ''
                }
                MenuProps={{
                  PaperProps: {
                    style: styles.selectMenu,
                  },
                }}
              >
                {Array.isArray(aftermarketClientList) &&
                  aftermarketClientList.map((client) => (
                    <MenuItem key={String(client.clientId)} value={String(client.clientId)}>
                      {client.client}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ textAlign: 'right', mt: '30px', mb: '30px', mr: '20px' }}>
          <Button
            variant='outlined'
            size='large'
            sx={{ mr: 2, borderRadius: '20px' }}
            color='error'
            onClick={onCloseAddDealerGroup}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              onCloseAddDealerGroup()
            }}
            variant='contained'
            sx={{ borderRadius: '20px' }}
            size='large'
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}

export default UserAftermarketClientAdd
